import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { MegaphoneIcon } from '@heroicons/react/24/outline'
import { ANNOUNCEMENT_FEED } from '../../graphql/announcement-queries'
import LoadingSpinner from '../../components/LoadingSpinner'
import AnnouncementRow from '../../components/announcements/AnnouncementRow'
import AnnouncementModal from '../../components/announcements/AnnouncementModal'
import { isUnread } from '../../types/announcement'
import type { Announcement, AnnouncementFeed } from '../../types/announcement'

const PAGE_SIZE = 20

type Tab = 'unread' | 'all'

const AnnouncementsFeedPage = () => {
  const [tab, setTab] = useState<Tab>('unread')
  const [selected, setSelected] = useState<Announcement | null>(null)

  const { data, loading, error, fetchMore } = useQuery(ANNOUNCEMENT_FEED, {
    variables: { limit: PAGE_SIZE, offset: 0 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const feed: AnnouncementFeed | undefined = data?.announcementFeed
  const announcements = feed?.announcements ?? []
  const unreadCount = feed?.unread_count ?? 0
  const hasMore = !!feed && announcements.length < feed.total

  const visible = tab === 'unread' ? announcements.filter(isUnread) : announcements

  // Keep the open modal in sync with refreshed feed data (read/ack markers).
  const selectedLive = selected
    ? announcements.find((a) => a.id === selected.id) ?? selected
    : null

  const loadMore = () =>
    fetchMore({
      variables: { limit: PAGE_SIZE, offset: announcements.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return {
          announcementFeed: {
            ...fetchMoreResult.announcementFeed,
            announcements: [
              ...prev.announcementFeed.announcements,
              ...fetchMoreResult.announcementFeed.announcements,
            ],
          },
        }
      },
    })

  const tabs: { key: Tab; label: string }[] = [
    { key: 'unread', label: `Unread${unreadCount ? ` (${unreadCount})` : ''}` },
    { key: 'all', label: 'All' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
        <p className="mt-1 text-sm text-gray-500">Updates from management — how we currently do things.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? 'border-primary-500 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading && announcements.length === 0 ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : error && announcements.length === 0 ? (
        <div className="card p-4 bg-red-50 border border-red-200 text-sm text-red-700">
          Failed to load announcements: {error.message}
        </div>
      ) : visible.length === 0 ? (
        <div className="card flex flex-col items-center justify-center gap-2 py-12 text-center text-gray-400">
          <MegaphoneIcon className="h-8 w-8 text-gray-300" />
          <p className="text-sm">{tab === 'unread' ? "You're all caught up." : 'No announcements yet.'}</p>
        </div>
      ) : (
        <div className="card p-2 divide-y divide-gray-100">
          {visible.map((a) => (
            <AnnouncementRow key={a.id} announcement={a} onOpen={setSelected} />
          ))}
        </div>
      )}

      {tab === 'all' && hasMore && (
        <div className="flex justify-center">
          <button onClick={() => loadMore()} disabled={loading} className="btn-secondary">
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      <AnnouncementModal
        announcement={selectedLive}
        onClose={() => setSelected(null)}
      />
    </div>
  )
}

export default AnnouncementsFeedPage
